import { FC } from 'react'
import { ResolvedFunction } from '../../types/functions'
import { ArrowRight, Layers } from 'lucide-react'

interface FunctionCategoryOverviewProps {
  moduleName: string
  categoryName: string
  functions: ResolvedFunction[]
  onSelectFunction: (fn: ResolvedFunction) => void
}

export const FunctionCategoryOverview: FC<FunctionCategoryOverviewProps> = ({
  moduleName,
  categoryName,
  functions,
  onSelectFunction
}) => {
  const getBadgeClass = (badge: string) => {
    switch (badge) {
      case 'FUNC':
        return 'ns-badge-func'
      case 'BROWSER':
        return 'ns-badge-browser'
      case 'ASYNC':
        return 'ns-badge-async'
      default:
        return 'ns-badge-default'
    }
  }

  return (
    <div className="ns-category-overview-page">
      {/* Header Banner */}
      <div className="ns-category-overview-header">
        <div className="ns-function-breadcrumbs">
          <span className="ns-breadcrumb-module">{moduleName}</span>
          <span className="ns-breadcrumb-sep">/</span>
          <span className="ns-breadcrumb-category">{categoryName}</span>
        </div>

        <div className="ns-category-overview-title-row">
          <div className="ns-category-icon-wrapper">
            <Layers size={22} className="text-forest" />
          </div>
          <div>
            <h2 className="ns-category-overview-title">{categoryName}</h2>
            <p className="ns-category-overview-subtitle">
              {functions.length} {functions.length === 1 ? 'function' : 'functions'} available in {moduleName}
            </p>
          </div>
        </div>
      </div>

      {/* Grid of Functions */}
      <div className="ns-category-cards-grid">
        {functions.map(fn => (
          <div
            key={fn.id}
            className="ns-category-fn-card"
            onClick={() => onSelectFunction(fn)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelectFunction(fn)
              }
            }}
          >
            <div className="ns-category-fn-header">
              <span className={`ns-function-badge ${getBadgeClass(fn.badge)}`}>
                {fn.badge}
              </span>
              <h3 className="ns-category-fn-name">{fn.displayName}</h3>
            </div>

            <p className="ns-category-fn-desc">
              {fn.summary || fn.description || 'No description available.'}
            </p>

            <div className="ns-category-fn-signature">
              <code>{fn.signatureString}</code>
            </div>

            <div className="ns-category-fn-footer">
              <span className="ns-category-param-count">
                {fn.parameters.length} {fn.parameters.length === 1 ? 'parameter' : 'parameters'}
              </span>
              <span className="ns-category-view-link">
                View documentation <ArrowRight size={14} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
